import { PAGES } from "@dune/types";
import { Toaster } from "@dune/ui";
import ReactPlugin from "@stagewise-plugins/react";
import { StagewiseToolbar } from "@stagewise/toolbar-react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import ChoosePassword from "./auth/register/ChoosePassword";
import ForgetPassword from "./auth/register/ForgetPassword";
import GetOriasDetails from "./auth/register/GetOriasDetails";
import Login from "./auth/register/Login";
import ResetPassword from "./auth/register/ResetPassword";
import SendEmail from "./auth/register/SendEmail";
import RegisterMainDetails from "./auth/register/register-main-details/RegisterMainDetails";
import RegisterSecondStepForSociety from "./auth/register/register-second-step-broker/RegisterSecondStepBroker";
import { DocusignSignatureModal } from "./component/docusign/DocusignSignatureModal";
import { BrokerRequireCompleteRegistration } from "./general/BrokerRequireCompleteRegistration";
import { InternalAppWrapper } from "./general/InternalAppWrapper";
import { PublicRoute } from "./general/PublicRoute";
import { RequireAuth } from "./general/RequireAuth";
import ConstructionSiteCompletePage from "./pages/public/ConstructionSiteCompletePage";
import DocumentTemplatesPage from "./pages/admin/documentTemplates/DocumentTemplatesPage";
import WebhooksPage from "./pages/admin/webhooks/WebhooksPage";
import EmailTemplateEditorPage from "./pages/admin/emailTemplates/EmailTemplateEditorPage";
import BinderDetails from "./pages/binders/BinderDetails";
import Binders from "./pages/binders/BindersList";
import BrokerList from "./pages/brokers/brokerList/BrokerList";
import BrokersPage from "./pages/brokers/brokersPage/BrokersPage";
import PartnershipRequestAnalysis from "./pages/brokers/partnershipRequestAnalysis/PartnershipRequestAnalysis";
import ClientDetailsPage from "./pages/clients/clientDetails/ClientDetailsPage";
import ClientsListPage from "./pages/clients/clientList/ClientsListPage";
import CommunicationListPage from "./pages/communications/communicationList/communicationListPage";
import ConstructionSiteBinders from "./pages/constructionSites/ConstructionSiteBinders";
import ConstructionSitePage from "./pages/constructionSites/ConstructionSitePage";
import { ConstructionSiteAnalysis } from "./pages/constructionSites/analysis/ConstructionSiteAnalysis";
import ConstructionSitesList from "./pages/constructionSites/constructionList/ConstructionSitesList";
import { ProjectReception } from "./pages/constructionSites/reception/ProjectReception";
import AddBuilderReceptionPage from "./pages/constructionSites/reception/participants/AddBuilderReceptionPage.tsx";
import AddDesignerReceptionPage from "./pages/constructionSites/reception/participants/AddDesignerReceptionPage.tsx";
import AddMaterialReceptionPage from "./pages/constructionSites/reception/participants/AddMaterialReceptionPage.tsx";
import EditBuilderReceptionPage from "./pages/constructionSites/reception/participants/EditBuilderReceptionPage";
import EditDesignerReceptionPage from "./pages/constructionSites/reception/participants/EditDesignerReceptionPage";
import EditMaterialReceptionPage from "./pages/constructionSites/reception/participants/EditMaterialReceptionPage";
import { ReceptionAnalysis } from "./pages/constructionSites/receptionAnalysis/ReceptionAnalysis";
import { ProjectSubmission } from "./pages/constructionSites/submission/ProjectSubmission";
import { AddBuilderToSubmissionPage } from "./pages/constructionSites/submission/participants/AddBuilderToSubmissionPage";
import { AddDesignerToSubmissionPage } from "./pages/constructionSites/submission/participants/AddDesignerToSubmissionPage";
import { AddMaterialToSubmissionPage } from "./pages/constructionSites/submission/participants/AddMaterialToSubmissionPage";
import { EditBuilderToSubmissionPage } from "./pages/constructionSites/submission/participants/EditBuilderToSubmissionPage";
import { EditDesignerToSubmissionPage } from "./pages/constructionSites/submission/participants/EditDesignerToSubmissionPage";
import { EditMaterialToSubmissionPage } from "./pages/constructionSites/submission/participants/EditMaterialToSubmissionPage";
import { ListContactsPage } from "./pages/contacts/ListContactsPage";
import ContractorDetailsPage from "./pages/contractors/contractorDetails/ContractorDetailsPage";
import ContractorsListPage from "./pages/contractors/contractorsList/ContractorsListPage";
import InsurersManagementPage from "./pages/insurers-management/InsurersManagementPage";
import ContractPage from "./pages/contracts/ContractPage";
import ContractsPage from "./pages/contracts/ContractsPage";
import Dashboard from "./pages/dashboard/Dashboard";
import DocuSignComplete from "./pages/docusign/DocuSignComplete";
import GFAConstructionSitePage from "./pages/gfa/GFAConstructionSitePage";
import GFAList from "./pages/gfa/gfaList/GFAList";
import ProfilePage from "./pages/profile/ProfilePage";
import PropositionPage from "./pages/propositions/PropositionPage";
import PropositionsPage from "./pages/propositions/PropositionsPage";
import ReceiptListPage from "./pages/receipts/ReceiptListPage";
import ReceiptPage from "./pages/receipts/ReceiptPage";
import { ListTasksPage } from "./pages/tasks/ListTasksPage";
import "galadrim-feedback/dist/styles.css";
import { FeedbackRootComponent } from "./utils/FeedbackRoot.tsx";
import { CreateProjectForm } from "./pages/constructionSites/projectForm/CreateProjectForm.tsx";
import { EditProjectForm } from "./pages/constructionSites/projectForm/EditProjectForm.tsx";
import { CreateGFAProjectForm } from "./pages/constructionSites/projectForm/CreateGFAProjectForm.tsx";
import { EditGFAProjectForm } from "./pages/constructionSites/projectForm/EditGFAProjectForm.tsx";
import ResourcesListPage from "./pages/resources/ResourcesListPage.tsx";

function App() {
	return (
		<BrowserRouter>
			<Routes>
				<Route element={<FeedbackRootComponent />}>
					<Route path={PAGES.RESET_PASSWORD} element={<ResetPassword />} />
					<Route
						path={PAGES.DOCUSIGN_COMPLETE}
						element={<DocuSignComplete />}
					/>
					<Route path={PAGES.ORIAS} element={<GetOriasDetails />} />
					<Route
						path={PAGES.REGISTER_BROKER_MAIN_DETAILS}
						element={<RegisterMainDetails />}
					/>
					<Route element={<PublicRoute />}>
						<Route path={PAGES.LOGIN} element={<Login />} />
						<Route path={PAGES.CHOOSE_PASSWORD} element={<ChoosePassword />} />
						<Route path={PAGES.SEND_EMAIL} element={<SendEmail />} />
						<Route path={PAGES.FORGET_PASSWORD} element={<ForgetPassword />} />
						<Route
							path={PAGES.CONSTRUCTION_SITE_COMPLETE.index}
							element={<ConstructionSiteCompletePage />}
						/>
					</Route>

					<Route element={<RequireAuth />}>
						<Route
							path={PAGES.REGISTER_BROKER_SECOND_STEP.index}
							element={<RegisterSecondStepForSociety />}
						/>
						<Route
							path={PAGES.REGISTER_BROKER_SECOND_STEP.withBrokerId}
							element={<RegisterSecondStepForSociety />}
						/>

						<Route element={<BrokerRequireCompleteRegistration />}>
							<Route path={PAGES.ROOT} element={<InternalAppWrapper />}>
								<Route index element={<Dashboard />} />
								<Route
									path={PAGES.PROJECTS.reception.participants.addDesigner}
									element={<AddDesignerReceptionPage />}
								/>
								<Route
									path={PAGES.PROJECTS.reception.participants.addBuilder}
									element={<AddBuilderReceptionPage />}
								/>
								<Route
									path={PAGES.PROJECTS.reception.participants.addMaterial}
									element={<AddMaterialReceptionPage />}
								/>
								<Route
									path={PAGES.PARTNERSHIP_REQUEST_ANALYSIS.index}
									element={<PartnershipRequestAnalysis />}
								/>
								<Route path={PAGES.BROKER_LIST} element={<BrokerList />} />
								<Route
									path={PAGES.BROKERS.show.index}
									element={<BrokersPage />}
								/>
								<Route
									path={PAGES.PROJECTS.index}
									element={<ConstructionSitesList />}
								/>
								<Route
									path={PAGES.PROJECTS.show.index}
									element={<ConstructionSitePage />}
								/>
								<Route
									path={PAGES.PROJECTS.create}
									element={<CreateProjectForm />}
								/>
								<Route
									path={PAGES.PROJECTS.edit.index}
									element={<EditProjectForm />}
								/>
								<Route
									path={PAGES.PROJECTS.submission.index}
									element={<ProjectSubmission />}
								/>
								<Route
									path={PAGES.PROJECTS.reception.index}
									element={<ProjectReception />}
								/>
								<Route
									path={PAGES.PROJECTS.receptionAnalysis.index}
									element={<ReceptionAnalysis />}
								/>
								<Route
									path={PAGES.PROJECTS.analysis.index}
									element={<ConstructionSiteAnalysis />}
								/>
								<Route
									path={PAGES.PROJECTS.submission.participants.addDesigner}
									element={<AddDesignerToSubmissionPage />}
								/>
								<Route
									path={PAGES.PROJECTS.submission.participants.editDesigner}
									element={<EditDesignerToSubmissionPage />}
								/>
								<Route
									path={PAGES.PROJECTS.submission.participants.addBuilder}
									element={<AddBuilderToSubmissionPage />}
								/>
								<Route
									path={PAGES.PROJECTS.submission.participants.editBuilder}
									element={<EditBuilderToSubmissionPage />}
								/>
								<Route
									path={PAGES.PROJECTS.submission.participants.addMaterial}
									element={<AddMaterialToSubmissionPage />}
								/>
								<Route
									path={PAGES.PROJECTS.submission.participants.editMaterial}
									element={<EditMaterialToSubmissionPage />}
								/>
								<Route
									path={PAGES.PROJECTS.reception.participants.editDesigner}
									element={<EditDesignerReceptionPage />}
								/>
								<Route
									path={PAGES.PROJECTS.reception.participants.editBuilder}
									element={<EditBuilderReceptionPage />}
								/>
								<Route
									path={PAGES.PROJECTS.reception.participants.editMaterial}
									element={<EditMaterialReceptionPage />}
								/>
								<Route
									path={PAGES.PROJECTS.binders.show.index}
									element={<ConstructionSiteBinders />}
								/>
								<Route
									path={PAGES.DOCUMENT_TEMPLATES}
									element={<DocumentTemplatesPage />}
								/>
								<Route path={PAGES.WEBHOOKS} element={<WebhooksPage />} />
								<Route
									path={PAGES.EMAIL_TEMPLATES.create}
									element={<EmailTemplateEditorPage />}
								/>
								<Route
									path={PAGES.EMAIL_TEMPLATES.edit.index}
									element={<EmailTemplateEditorPage />}
								/>
								<Route
									path={PAGES.EMAIL_TEMPLATES.preview.index}
									element={<EmailTemplateEditorPage />}
								/>
								<Route path={PAGES.BINDERS.index} element={<Binders />} />
								<Route
									path={PAGES.BINDERS.show.index}
									element={<BinderDetails />}
								/>
								<Route
									path={PAGES.CONTACTS.index}
									element={<ListContactsPage />}
								/>
								<Route path={PAGES.TASKS} element={<ListTasksPage />} />
								<Route
									path={PAGES.CLIENTS.index}
									element={<ClientsListPage />}
								/>
								<Route
									path={PAGES.CLIENTS.show.index}
									element={<ClientDetailsPage />}
								/>
								<Route
									path={PAGES.COMMUNICATIONS}
									element={<CommunicationListPage />}
								/>
								<Route
									path={PAGES.RESOURCES}
									element={<ResourcesListPage />}
								/>
								<Route path={PAGES.PROFILE} element={<ProfilePage />} />
								<Route
									path={PAGES.PROPOSALS.index}
									element={<PropositionsPage />}
								/>
								<Route
									path={PAGES.PROPOSALS.show.index}
									element={<PropositionPage />}
								/>
								<Route
									path={PAGES.CONTRACTS.index}
									element={<ContractsPage />}
								/>
								<Route
									path={PAGES.CONTRACTS.show.index}
									element={<ContractPage />}
								/>
								<Route
									path={PAGES.CONTRACTORS.index}
									element={<ContractorsListPage />}
								/>
								<Route
									path={PAGES.CONTRACTORS.show.index}
									element={<ContractorDetailsPage />}
								/>
								<Route
									path={PAGES.INSURERS_MANAGEMENT}
									element={<InsurersManagementPage />}
								/>
								<Route path={PAGES.GFA.index} element={<GFAList />} />
								<Route
									path={PAGES.GFA.create}
									element={<CreateGFAProjectForm />}
								/>
								<Route
									path={PAGES.GFA.edit.index}
									element={<EditGFAProjectForm />}
								/>
								<Route
									path={PAGES.GFA.show.index}
									element={<GFAConstructionSitePage />}
								/>
								<Route
									path={PAGES.RECEIPTS.index}
									element={<ReceiptListPage />}
								/>
								<Route
									path={PAGES.RECEIPTS.show.index}
									element={<ReceiptPage />}
								/>
							</Route>
						</Route>
					</Route>
				</Route>
			</Routes>

			<Toaster />
			<DocusignSignatureModal />
			<StagewiseToolbar config={{ plugins: [ReactPlugin] }} />
		</BrowserRouter>
	);
}

export default App;
